/**
 * Xamun Owner admin — tenant list + provision form.
 *
 * Phase A of platform-gaps Gap 1. Reachable at `/admin/tenants` (NOT inside
 * the workspace shell — Xamun staff are platform-level operators, not
 * tenants of-record). Behind requireXamunAdmin on the API side; the page
 * surfaces 403 cleanly if the signed-in user isn't on the allow-list.
 */

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import type { ApiError} from "../../api/client";
import { apiGet, apiPost } from "../../api/client";
import { AdminShell } from "../../components/admin/AdminShell";

interface TenantSummary {
  id: string;
  name: string;
  companyId: string;
  state: "active" | "paused" | "archived";
  cxoRoleTag: string;
  ownerUserId: string;
  ownerEmail: string | null;
  createdAt: string;
  memberCount: number;
  playCount: number;
  spotCount: number;
}

interface TenantList {
  tenants: TenantSummary[];
  total: number;
}

export function AdminTenants() {
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [stateFilter, setStateFilter] = useState<"" | "active" | "paused" | "archived">("");
  const [formOpen, setFormOpen] = useState(false);

  const params = new URLSearchParams();
  if (q.trim()) params.set("q", q.trim());
  if (stateFilter) params.set("state", stateFilter);
  const listQ = useQuery<TenantList, ApiError>({
    queryKey: ["admin-tenants", q, stateFilter],
    queryFn: () => apiGet(`/api/admin/tenants${params.toString() ? `?${params}` : ""}`),
    retry: false,
  });

  return (
    <AdminShell>
    <main className="review-page" style={{ margin: "0 auto" }}>
      <p className="review-eyebrow">xamun owner</p>
      <h1 className="review-title">Tenants</h1>
      <p className="review-meta">
        Provision new workspaces, suspend or archive existing ones, manage
        feature flags. Every action is logged.
      </p>

      {listQ.error?.status === 403 && (
        <div className="review-banner error" style={{ marginTop: 16 }}>
          You're not on the Xamun admin allow-list. Set <code>XAMUN_ADMIN_EMAILS</code>
          {" "}in the API env to include your address.
        </div>
      )}

      <div
        style={{
          marginTop: 18,
          display: "flex",
          gap: 8,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <input
          className="review-input"
          placeholder="Search by name / company id"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          style={{ flex: "1 1 240px" }}
        />
        <select
          className="review-input"
          value={stateFilter}
          onChange={(e) => setStateFilter(e.target.value as typeof stateFilter)}
          style={{ flex: "0 0 180px" }}
        >
          <option value="">All states</option>
          <option value="active">Active</option>
          <option value="paused">Paused</option>
          <option value="archived">Archived</option>
        </select>
        <button
          type="button"
          className="review-btn approve"
          onClick={() => setFormOpen((v) => !v)}
        >
          {formOpen ? "Cancel" : "+ Provision tenant"}
        </button>
      </div>

      {formOpen && (
        <ProvisionForm
          onCancel={() => setFormOpen(false)}
          onCreated={async () => {
            setFormOpen(false);
            await qc.invalidateQueries({ queryKey: ["admin-tenants"] });
          }}
        />
      )}

      <section className="review-section">
        <h2 className="review-section-title">
          {listQ.data ? `${listQ.data.total} tenants` : "Loading…"}
        </h2>
        {listQ.isLoading ? (
          <div style={{ color: "var(--text-3)" }}>Loading…</div>
        ) : listQ.error && listQ.error.status !== 403 ? (
          <div className="review-banner error">
            Could not load tenants ({listQ.error.status}).
          </div>
        ) : !listQ.data || listQ.data.tenants.length === 0 ? (
          <div className="ho-empty">No tenants match.</div>
        ) : (
          <ul className="review-comment-list">
            {listQ.data.tenants.map((t) => (
              <li key={t.id} className="review-comment">
                <div className="review-comment-head">
                  <strong className="author">{t.name}</strong>
                  <span className={`review-comment-decision ${stateTone(t.state)}`}>
                    {t.state}
                  </span>
                  <span style={{ color: "var(--text-3, #64748b)" }}>
                    {t.cxoRoleTag}
                  </span>
                  <span style={{ marginLeft: "auto", fontSize: 11 }}>
                    {new Date(t.createdAt).toLocaleDateString()}
                  </span>
                </div>
                <div style={{ marginTop: 6, fontSize: 13, color: "var(--text-2, #475569)" }}>
                  <strong>Owner:</strong> {t.ownerEmail ?? t.ownerUserId} ·{" "}
                  <strong>{t.memberCount}</strong> members ·{" "}
                  <strong>{t.playCount}</strong> plays ·{" "}
                  <strong>{t.spotCount}</strong> spots ·{" "}
                  <code style={{ fontSize: 11 }}>{t.companyId}</code>
                </div>
                <div style={{ marginTop: 6 }}>
                  <Link
                    to={`/admin/tenants/${t.id}`}
                    style={{ fontSize: 12, color: "var(--accent, #6366f1)" }}
                  >
                    Open detail →
                  </Link>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </main>
    </AdminShell>
  );
}

function stateTone(state: "active" | "paused" | "archived"): "approve" | "return" | "note" {
  if (state === "active") return "approve";
  if (state === "archived") return "return";
  return "note";
}

function ProvisionForm({
  onCancel,
  onCreated,
}: {
  onCancel: () => void;
  onCreated: () => void;
}) {
  const [name, setName] = useState("");
  const [cxoRoleTag, setCxoRoleTag] = useState("cxo");
  const [ownerEmail, setOwnerEmail] = useState("");
  const [ownerDisplayName, setOwnerDisplayName] = useState("");
  const [companyId, setCompanyId] = useState("");
  const [playstudioOnly, setPlaystudioOnly] = useState(false);

  const provision = useMutation<unknown, ApiError, void>({
    mutationFn: () =>
      apiPost("/api/admin/tenants", {
        name: name.trim(),
        cxo_role_tag: cxoRoleTag.trim() || "cxo",
        owner_email: ownerEmail.trim(),
        ...(ownerDisplayName.trim() && { owner_display_name: ownerDisplayName.trim() }),
        ...(companyId.trim() && { company_id: companyId.trim() }),
        ...(playstudioOnly && { playstudio_only: true }),
      }),
    onSuccess: onCreated,
  });

  const canSubmit =
    name.trim().length >= 2 &&
    ownerEmail.trim().length > 3 &&
    (companyId.trim().length > 0 || playstudioOnly);

  return (
    <section className="review-form" style={{ marginTop: 18 }}>
      <h2 className="review-section-title" style={{ marginBottom: 12 }}>
        Provision new tenant
      </h2>
      <div className="spotted-form-row">
        <label className="spotted-field">
          <span>Workspace name</span>
          <input
            className="review-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="ACME Corp · COO"
          />
        </label>
        <label className="spotted-field">
          <span>CXO role tag</span>
          <input
            className="review-input"
            value={cxoRoleTag}
            onChange={(e) => setCxoRoleTag(e.target.value)}
            placeholder="cxo · coo · cfo · cmo …"
          />
        </label>
      </div>
      <div className="spotted-form-row" style={{ marginTop: 12 }}>
        <label className="spotted-field">
          <span>Owner email</span>
          <input
            className="review-input"
            type="email"
            value={ownerEmail}
            onChange={(e) => setOwnerEmail(e.target.value)}
            placeholder="ceo@acme.com"
          />
        </label>
        <label className="spotted-field">
          <span>Owner display name (optional)</span>
          <input
            className="review-input"
            value={ownerDisplayName}
            onChange={(e) => setOwnerDisplayName(e.target.value)}
            placeholder="Sam Chen"
          />
        </label>
      </div>
      <label className="review-field">
        <span className="review-field-label">
          <strong>XI company id</strong>
          <span className="review-field-hint">
            canonical id shared with XI — required unless this is a PlayStudio-only tenant
          </span>
        </span>
        <input
          className="review-input"
          value={companyId}
          onChange={(e) => {
            setCompanyId(e.target.value);
            if (e.target.value.trim()) setPlaystudioOnly(false);
          }}
          placeholder="co_acme_corp"
          disabled={playstudioOnly}
        />
      </label>

      <label
        className="review-field"
        style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10 }}
      >
        <input
          type="checkbox"
          checked={playstudioOnly}
          onChange={(e) => {
            setPlaystudioOnly(e.target.checked);
            if (e.target.checked) setCompanyId("");
          }}
          style={{ width: "auto" }}
        />
        <span style={{ fontSize: 13 }}>
          <strong>PlayStudio-only tenant</strong>{" "}
          <span style={{ color: "var(--text-3, #64748b)" }}>
            — greenfield, no XI presence. Generates a co_pso_* id.
          </span>
        </span>
      </label>

      {provision.error && (
        <div className="review-banner error" style={{ marginTop: 12 }}>
          {provision.error.status === 403
            ? "You're not on the Xamun admin allow-list."
            : `Could not provision (${provision.error.status}).`}
        </div>
      )}

      <div className="review-actions" style={{ marginTop: 16 }}>
        <button
          type="button"
          className="review-btn approve"
          disabled={!canSubmit || provision.isPending}
          onClick={() => provision.mutate()}
        >
          {provision.isPending ? "Provisioning…" : "Provision"}
        </button>
        <button type="button" className="review-btn note" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </section>
  );
}
